import { Component, OnInit, ElementRef, ChangeDetectorRef, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTreeModule } from '@angular/material/tree';
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { ChangeTitleComponent } from '../change-title/change-title.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { SendMailComponent } from '../send-mail/send-mail.component';
import { TaskComponent } from '../task/task.component';
import { RevisionsComponent } from '../revisions/revisions.component';
import { UserDTO } from 'src/app/interfaces/user';
import { CalendarDTO, createCalendar } from 'src/app/interfaces/calendar';
import { SettingsDTO } from 'src/app/interfaces/settings';
import { createNewNote, createNote, NoteDTO } from 'src/app/interfaces/note';
import { EventViewDTO } from 'src/app/interfaces/event';
import { CalendarService } from 'src/app/services/calendar.service';
import { NoteService } from 'src/app/services/note.service';
import { combineLatest, debounceTime, distinctUntilChanged, filter, firstValueFrom, Subject, Subscription, timer } from 'rxjs';
import { EventService } from 'src/app/services/event.service';
import { TaskDTO, createTask } from 'src/app/interfaces/task';
import { TaskService } from 'src/app/services/task.service';
import { CommonModule, DatePipe, NgClass, NgForOf, NgIf, NgStyle } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTabsModule } from '@angular/material/tabs';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressBar } from '@angular/material/progress-bar';
import { VisibilityService } from 'src/app/services/visibility.service';
import { FunctionsService } from 'src/app/services/functions.service';
import { RecordingService } from 'src/app/services/recording.service';
import { createRecording } from 'src/app/interfaces/recording';
import { MatMenuModule } from '@angular/material/menu';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Image as TipTapImage } from '@tiptap/extension-image';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { CustomDiv } from 'src/app/extensions/customDiv';
import { RRule, RRuleSet } from 'rrule';
import { CustomParagraphAttributes } from 'src/app/extensions/CustomParagraphAttributes';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { HasFeatureDirective } from 'src/app/directives/hasFeature';

@Component({
  selector: 'app-notes',
  imports: [DatePipe, MatIconModule, NgClass, FormsModule, MatTreeModule, MatFormFieldModule, NgStyle, MatProgressSpinnerModule,
    MatTabsModule, ReactiveFormsModule, NgIf, MatInputModule, NgForOf, MatOptionModule, MatTooltipModule, MatProgressBar, MatMenuModule,
    CommonModule, MatSelectModule, HasFeatureDirective
  ],
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss'],
})
export class NotesComponent implements OnInit {

  @ViewChild('editorElement') set editorElement(element: ElementRef<HTMLDivElement> | undefined) {
    if (element && element.nativeElement) {
      // Als er al een oude editor was die aan een oud HTML element hing: vernietig deze eerst
      if (this.editor) {
        this.editor.destroy();
      }
      // Initialiseer een verse TipTap instantie direct gekoppeld aan het NIEUWE HTML-element
      this.initializeEditor(element.nativeElement);
    } else if (!element && this.editor) {
      // Als de DOM wrapper verdwijnt (bijv. bij een folder of leeg scherm)
      this.editor.destroy();
      (this.editor as any) = null;
    }
  }
  readonly calendarColors = [
    '#3378cc', '#308527', '#a50a17', '#00a3cc', '#f29100',
    '#7b1fa2', '#546e7a', '#263238', '#ff5252', '#00bfa5',
  ];

  // User & Auth
  user: UserDTO;
  isLoggedIn = false;
  selectedCalendarForEventOverview: CalendarDTO = {
    id: -1, userId: -1, calendarIntegrationId: -1, calendarIntegrationName: '',
    externalCalendarId: '', summary: '', showInDashboard: false, showInNotes: false, uuid: '', taskUuid: '', color: '', url: ''
  };
  settings: SettingsDTO;
  calendars: CalendarDTO[] = [];
  addingFolderCalendarId: number = -1;
  addingNoteParentId: string | number | null = null;
  filterCalendars: CalendarDTO[] = [];

  // Editor State
  archived: boolean = false;
  protected: boolean = true;

  currentNoteIsFolder: boolean = false;
  hasRevisions: boolean = false;
  currentNote: NoteDTO | null = null;

  // Data
  notes: NoteDTO[] = [];
  archivedNotes: NoteDTO[] = [];
  searchResults: NoteDTO[] = [];
  upcomingEvents: EventViewDTO[] = [];
  selectedTabIndex: number = 0;
  searchQuery: string = '';
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  // Tree Controls
  selection = new SelectionModel<NoteDTO>(false, [], true, (a, b) => a?.id === b?.id);

  // Drag & Drop State
  dragNode: NoteDTO | null = null;
  dragNodeExpandOverWaitTimeMs = 300;
  dragNodeExpandOverNode: any;
  dragNodeExpandOverTime: number = 0;

  public isAddingRoot = false;
  private snackBar = inject(MatSnackBar);
  public isSearching = false;
  private originalData: any[] = [];
  public isSyncing = false;

  recordingStatus: string = "stopped";
  recordingSeconds: number = 0;
  recordingDuration: string = '00:00';
  private timerInterval: any = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private mediaStream: MediaStream | null = null;

  private eventRefreshSub?: Subscription;
  private readonly EVENT_REFRESH_INTERVAL = 5 * 60 * 1000;
  private lastRefreshTime: number = 0;
  private saveNoteTimerActive: boolean = false;
  public editor!: Editor;

  notesByCalendar: Map<number, NoteDTO[]> = new Map();
  loadingCalendars: Set<number> = new Set();
  expandedFolderIds: Set<number> = new Set<number>();
  expandedCalendarIds: Set<number> = new Set<number>();

  // Tree Predicates
  hasChild = (_: number, node: NoteDTO) => node.noteType === 'folder';
  hasNoContent = (_: number, node: NoteDTO) => node.title === '';

  @ViewChild('rootValue') rootInput!: ElementRef;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tokenStorageService: TokenStorageService,
    private matDialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private calendarService: CalendarService,
    private noteService: NoteService,
    private eventService: EventService,
    private taskService: TaskService,
    private visibilityService: VisibilityService,
    public functions: FunctionsService,
    private recordingService: RecordingService
  ) {
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  async ngOnInit() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if (!this.isLoggedIn) {
      this.router.navigate(['home']);
      return;
    }

    this.user = this.tokenStorageService.getUser();

    this.getAllCalendars();

    // Deep link support via ID
    this.route.queryParamMap.subscribe(async queryParams => {
      const urlId = queryParams.get("id");
      await this.selectNoteFromUrlId(urlId);
    });

    this.getUpcomingEvents();
    this.calendarRefresh();
    this.initializeSearchSubscription();
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.cleanupMediaStream();
    this.editor?.destroy();
  }

  initializeSearchSubscription() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.executeSearch(query);
    });
  }

  initializeEditor(element: HTMLElement) {
    this.editor = new Editor({
      element: element,
      extensions: [
        StarterKit.configure({
          heading: {
            HTMLAttributes: {
              style: null,
            },
          },
        }),
        TextStyle,
        CustomDiv,
        CustomParagraphAttributes,
        TipTapImage,
        TableRow,
        TableHeader,
        TableCell,
        TextAlign.configure({ types: ['heading', 'paragraph'] })
      ],
      content: this.currentNote?.stringText || '',
      onUpdate: ({ editor }) => {
        if (this.currentNote && !this.currentNoteIsFolder) {
          this.currentNote.stringText = editor.getHTML();
          this.currentNote.dirty = true;
          this.autoSaveNote();
        }
      },
      onBlur: () => {
        this.onBlurEditor();
      }
    });
    this.cdr.detectChanges();
  }

  async getAllCalendars() {
    this.calendars = await firstValueFrom(this.calendarService.getCalendarsForNotes());
    this.filterCalendars = [...this.calendars];
    this.filterCalendars.unshift({
      id: -1,
      userId: this.user.id,
      calendarIntegrationId: -1,
      calendarIntegrationName: '',
      externalCalendarId: '',
      summary: '<All>',
      showInDashboard: false,
      showInNotes: false,
      uuid: '',
      taskUuid: '',
      color: '',
      url: ''
    });
  }

  autoSaveNote() {
    if (this.currentNote && this.currentNote.dirty && !this.saveNoteTimerActive) {
      this.saveNoteTimerActive = true;
      setTimeout(() => {
        this.saveNote(this.currentNote);
        this.saveNoteTimerActive = false;
      }, 10000)
    }

  }

  calendarRefresh() {
    this.eventRefreshSub = combineLatest([
      this.visibilityService.isActive$,
      timer(0, 30000)
    ]).pipe(
      // Alleen doorgaan als de app in focus is
      filter(([isActive, _]) => isActive),
      // Alleen doorgaan als het langer dan 5 min geleden is (of de 1e keer)
      filter(() => {
        const rightNow = Date.now();
        return (rightNow - this.lastRefreshTime) >= this.EVENT_REFRESH_INTERVAL;
      })
    ).subscribe(() => {
      this.refreshEventData();
    });

  }

  onCalendarSelectChange(calendar: CalendarDTO | null): void {
    this.selectedCalendarForEventOverview = calendar;
    this.getUpcomingEvents();
  }

  refreshEventData() {
    this.lastRefreshTime = Date.now();
    setTimeout(() => this.getUpcomingEvents(), 0);
  }

  getCalendarLabel(calendar: CalendarDTO): string {
    if (!calendar) return '';
    const duplicates = this.calendars.filter(c => c.summary === calendar.summary);
    if (duplicates.length > 1 && calendar.calendarIntegrationName) {
      return `${calendar.summary} (${calendar.calendarIntegrationName})`;
    }
    return calendar.summary;
  }

  /* --- Note Detail Logic --- */
  async showNoteDetails(selected: NoteDTO) {
    if (this.currentNote && this.currentNote.dirty) {
      await this.saveNote(this.currentNote);
    }

    if (selected.noteType !== 'folder') {
      selected = await firstValueFrom(this.noteService.getNote(selected.id));
    }

    this.selection.select(selected);
    this.currentNote = selected;
    this.currentNoteIsFolder = selected.noteType === 'folder';
    this.archived = this.currentNote.parentNoteName === 'Archive';
    // 1. Dwing Angular om de HTML-template (inclusief *ngIf="currentNote && !currentNoteIsFolder") TEKENEN
    this.cdr.detectChanges();

    // 3. Nu het HTML element er gegarandeerd is: vul de Tiptap content
    if (this.editor && !this.currentNoteIsFolder) {
      this.editor.commands.setContent(this.currentNote?.stringText || '');
    }

    if (!this.currentNoteIsFolder) {
      this.getRevisions();
    } else {
      this.hasRevisions = false;
    }

    this.cdr.detectChanges();
  }

  clearCurrentNote() {
    this.currentNote = null;
    this.archived = false;
    this.selection.clear();
  }

  // Ondersteunt deep-linking (bijv. vanuit een e-mail naar een specifieke note)
  async selectNoteFromUrlId(id: string) {
    const noteId = Number(id);
    if (isNaN(noteId)) return;
    if (noteId == 0) return;

    try {
      // Haal de specifieke notitie direct op uit de backend
      const targetNote = await firstValueFrom(this.noteService.getNote(noteId));
      if (!targetNote) return;

      this.expandTreeAndSelectNote(targetNote);

    } catch (error) {
      console.error(`Could not open note with ID ${id} from URL:`, error);
    }
  }

  // Checkt of er revisies zijn voor de geselecteerde note
  async getRevisions() {
    if (this.currentNote) {
      const count = await firstValueFrom(this.noteService.hasRevisions(this.currentNote.calendarId, this.currentNote.uuid));
      if (count) {
        this.hasRevisions = count > 0;
      } else {
        this.hasRevisions = false;
      }
    }
  }

  /* --- Data fetching --- */
  private expandEventsToUpcoming(events: EventViewDTO[]): EventViewDTO[] {
    const upcomingList: EventViewDTO[] = [];

    const lookbackHours = this.settings?.lookbackHours ?? 2;
    const outlookHours = this.settings?.outlookHours ?? 72;

    const nowMs = Date.now();
    const windowStartMs = nowMs - (lookbackHours * 60 * 60 * 1000);
    const windowEndMs = nowMs + (outlookHours * 60 * 60 * 1000);

    for (const event of events) {
      if (!event.startDate || !event.endDate) continue;
      if (event.allDay) continue;

      let exceptionDates = new Set<number>();

      const start = new Date(event.startDate);
      const end = new Date(event.endDate);
      const durationMs = end.getTime() - start.getTime();

      // 3. NIET-HERHALENDE EVENTS
      if (!event.rrule) {
        if (end.getTime() >= windowStartMs && start.getTime() <= windowEndMs) {
          upcomingList.push(event);
        }
        continue;
      }

      // 4. HERHALENDE EVENTS (RRULE)
      try {
        const utcDtStart = new Date(Date.UTC(
          start.getUTCFullYear(),
          start.getUTCMonth(),
          start.getUTCDate(),
          start.getUTCHours(),
          start.getUTCMinutes()
        ));

        const ruleOptions = RRule.parseString(event.rrule);
        ruleOptions.dtstart = utcDtStart;

        if (ruleOptions.freq === RRule.YEARLY && ruleOptions.bymonthday && !ruleOptions.bymonth) {
          ruleOptions.bymonth = [start.getMonth() + 1];
        }

        const rruleSet = new RRuleSet();
        rruleSet.rrule(new RRule(ruleOptions));

        if (event.exdate) {
          exceptionDates = this.functions.parseICalExdatesToEpoch(event.exdate);
        }

        // Zoek uitsluitend binnen het venster (met kleine marge voor tijdszones)
        const searchStart = new Date(windowStartMs - (12 * 60 * 60 * 1000));
        const searchEnd = new Date(windowEndMs + (12 * 60 * 60 * 1000));

        const occurrences = rruleSet.between(searchStart, searchEnd, true);

        occurrences.forEach(occ => {
          const occStart = new Date(Date.UTC(
            occ.getUTCFullYear(),
            occ.getUTCMonth(),
            occ.getUTCDate(),
            occ.getUTCHours(),
            occ.getUTCMinutes()
          ));

          if (exceptionDates.has(occStart.getTime())) {
            return;
          }

          const occStartMs = occ.getTime();
          const occEndMs = occStartMs + durationMs;

          // Controleer of deze specifieke HERHALING binnen het venster valt
          if (occEndMs >= windowStartMs && occStartMs <= windowEndMs) {
            upcomingList.push({
              ...event,
              // Overschrijf de master-datum expliciet met de datum van déze herhaling!
              startDate: new Date(occStartMs).toISOString() as any,
              endDate: new Date(occEndMs).toISOString() as any
            });
          }

        });

      } catch (err) {
        console.error(`Fout bij verwerken RRULE voor event ${event.id}:`, err);
        upcomingList.push({
          ...event,
          // Overschrijf de master-datum expliciet met de datum van déze herhaling!
          startDate: start,
          endDate: end
        })
      }
    }

    return upcomingList.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  }

  async getUpcomingEvents() {
    if (!this.selectedCalendarForEventOverview) return;
    this.isSyncing = true;

    try {
      // Gebruik dezelfde service call als in het dashboard
      const rawEvents = await firstValueFrom(
        this.eventService.getUpcomingEvents(this.selectedCalendarForEventOverview.id)
      );

      this.upcomingEvents = this.expandEventsToUpcoming(rawEvents);
    } catch (error) {
    } finally {
      this.isSyncing = false;
    }
  }

  scrollIntoView(note: NoteDTO) {
    setTimeout(() => {
      const targetElement = document.querySelector(`[data-id="${note.id}"]`);
      if (targetElement) {
        targetElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 250);
  }

  getTimeZone(): string {
    if (this.settings?.timeZone) {
      return this.settings.timeZone;
    }
    return 'Europe/Amsterdam';
  }

  handleDragOver(event: DragEvent, node: NoteDTO) {
    event.preventDefault();

    // Automatisch uitklappen van folders tijdens drag
    if (node.noteType === 'folder') {
      if (this.dragNodeExpandOverNode && node === this.dragNodeExpandOverNode) {
        if ((Date.now() - this.dragNodeExpandOverTime) > this.dragNodeExpandOverWaitTimeMs) {
        }
      } else {
        this.dragNodeExpandOverNode = node;
        this.dragNodeExpandOverTime = Date.now();
      }
    }
  }

  /* --- Drag & Drop --- */
  handleDragEnd(event: DragEvent) {
    this.dragNode = null;
    this.dragNodeExpandOverNode = null;
    this.dragNodeExpandOverTime = 0;
    event.preventDefault();
  }

  handleDrop(event: DragEvent, node: NoteDTO) {
    if (this.dragNode && node !== this.dragNode) {
      const droppedNote = this.dragNode;
      this.removeFromList(droppedNote, this.notes);

      droppedNote.parentNoteId = node.id;
      droppedNote.dirty = true;

      node.notes = node.notes || [];
      node.notes.push(droppedNote);

      this.saveNote(droppedNote);
    }
    this.handleDragEnd(event);
  }

  /* --- Item Beheer --- */
  removeNewItem(note: NoteDTO, title: string) {
    if (this.notes) {
      this.removeFromList(note, this.notes);
      if (note.id > 0) {
        this.noteService.deleteNote(note);
      }
    }
  }

  /* --- Dialogen & Acties --- */
  showRevisions() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.id = "AddTaskFromNote";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.width = "1200px"
    dialogConfig.height = "600px";
    dialogConfig.data = this.currentNote;
    const modalDialog = this.matDialog.open(RevisionsComponent, dialogConfig);

  }

  async addTask() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.id = "AddTaskFromNote";
    dialogConfig.panelClass = "squaredDialog";
    dialogConfig.height = "530px";
    const title = await this.generateTitleForNote();
    const text = this.getSelectedText();
    dialogConfig.data = createTask(this.currentNote.calendarId, title, text, "Not started", new Date(), new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), new Date(), "Normal", 0, this.currentNote.id);
    dialogConfig.width = "750px";
    const modalDialog = this.matDialog.open(TaskComponent, dialogConfig);
    modalDialog.afterClosed().subscribe(data => {
      if (data && data.task) {
        var task = data.task;
        task.calendarId = this.currentNote.calendarId;
        task.noteId = this.currentNote.id;
        task.sequence = 0;
        this.saveTask(task);
      }
    });
  }

  async generateTitleForNote() {
    const data = await firstValueFrom(this.taskService.getNumberOfTasksForNote(this.currentNote.id));
    if (data) {
      return "Task " + (Number(data) + 1) + ": " + this.currentNote.title;
    } else {
      return "Task: " + this.currentNote.title;
    }
  }

  getSelectedText(): string {
    if (!this.editor) return '';
    const { from, to } = this.editor.state.selection;
    return this.editor.state.doc.textBetween(from, to, ' ');
  }

  async saveTask(task: TaskDTO) {
    const data = await firstValueFrom(this.taskService.saveTask(task.id, createTask(task.calendarId, task.title, task.text, task.status, task.creationDate, task.dueDate, task.statusDate, task.priority, task.sequence, task.noteId)));
  }

  mail() {
    if (this.currentNote) {
      this.matDialog.open(SendMailComponent, {
        width: '900px',
        height: '600px',
        panelClass: 'squaredDialog',
        data: this.currentNote
      });
    }
  }

  onRename() {
    if (this.currentNote) {

      const selectedId = this.currentNote.id;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = this.currentNote;
      dialogConfig.width = "600px";

      const modalDialog = this.matDialog.open(ChangeTitleComponent, dialogConfig);
      modalDialog.afterClosed().subscribe(async data => {
        if (data && data.data) {
          this.currentNote!.title = data.data;
          this.currentNote!.dirty = true;
          await this.saveNote(this.currentNote);
          this.loadNotesForCalendar(this.currentNote.calendarId);
        }
      });
    }
  }

  /* --- CRUD Operations --- */
  async saveNote(note: NoteDTO | null) {
    if (note && note.dirty) {
      try {
        note.dirty = false;
        const data = await firstValueFrom(this.noteService.saveNote(note.id, createNote(note.calendarId, note.eventId, note.title, note.stringText, note.noteType, note.archived, note.recurring, note.participants, note.hiddenTag, note.deleted, note.parentNoteId, note.endDate, note.uuid, note.lastModifiedTime, note.version)));
      } catch (error) {
        note.dirty = true;
      }
    }
  }

  onBlurEditor() {
    if (this.currentNote && !this.currentNoteIsFolder && this.currentNote.dirty) {
      this.saveNote(this.currentNote);
    }
  }

  async onArchive() {
    if (!this.currentNote) return;
    this.currentNote.dirty = true;
    try {
      const data = await firstValueFrom(this.noteService.toogleArchive(this.currentNote.id));
      this.loadNotesForCalendar(this.currentNote.calendarId);
      this.clearCurrentNote();
    } catch (error) {
      this.currentNote.dirty = true;
    }

  }

  onDelete() {
    if (!this.currentNote) return;

    const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
      width: '400px',
      panelClass: 'custom-dialog-container',
      data: { title: 'Delete Note', message: `Are you sure you want to delete "${this.currentNote.title}"?` }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result && this.currentNote) {
        this.deleteNote();
      }
    });
  }

  async deleteNote() {
    const calId = this.currentNote.calendarId;
    const data = await firstValueFrom(this.noteService.deleteNote(this.currentNote));
    this.removeFromList(this.currentNote, this.notes);
    this.clearCurrentNote();
    this.loadNotesForCalendar(calId);
  }

  private removeFromList(noteToDelete: NoteDTO, list: NoteDTO[]) {
    const index = list.findIndex(n => n.id === noteToDelete.id);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.forEach(parent => {
        if (parent.notes) this.removeFromList(noteToDelete, parent.notes);
      });
    }
  }

  startAddingRoot(calendar: CalendarDTO) {
    this.onSearch('');
    this.isAddingRoot = true;
    this.addingFolderCalendarId = calendar.id;
    // We wachten één 'tick' zodat Angular de *ngIf heeft verwerkt 
    // en het veld daadwerkelijk in de HTML staat.
    setTimeout(() => {
      if (this.rootInput) {
        this.rootInput.nativeElement.focus();
      }
    }, 0);
  }

  saveRootItem(title: string) {
    let newFolder = this.getNewNote("folder", null);
    newFolder.calendarId = this.addingFolderCalendarId;
    this.saveNewItem(newFolder, title);
    this.isAddingRoot = false;
    this.addingFolderCalendarId = -1;
    this.snackBar.open('Folder ' + title + ' created', 'OK', { duration: 2000 });
  }

  addNewFolder() {
    const newFolder = this.getNewNote('folder', null);
    this.notes.push(newFolder);
  }

  addNewItem(node: any) {
    // Zorg dat de folder geopend wordt zodra we een item toevoegen
    if (node.noteType === 'folder') {
      this.expandedFolderIds.add(node.id);
    }
    this.addingNoteParentId = node.id;
  }

  cancelAddingItem() {
    this.addingNoteParentId = null;
  }

  getNewNote(type: string, parent: NoteDTO | null): NoteDTO {
    const note = createNewNote(-1, type, parent ? parent.id : null); //TODO
    if (parent) {
      note.calendarId = parent.calendarId;
      note.parentNoteId = parent.id;
      parent.notes = parent.notes || [];
      parent.notes.push(note);
    }
    return note;
  }

  async saveNewItem(parent: NoteDTO, title: string) {
    if (!title || !title.trim()) {
      this.cancelAddingItem();
      return;
    }

    const note = this.getNewNote('note', parent)
    note.title = title;
    try {
      const savedNote = await firstValueFrom(
        this.noteService.saveNote(
          note.id,
          createNote(note.calendarId, note.eventId, note.title, note.stringText, note.noteType, note.archived, note.recurring, note.participants,
            note.hiddenTag, note.deleted, note.parentNoteId, note.endDate, note.uuid, note.lastModifiedTime, note.version)
        )
      );
      this.addingNoteParentId = null;

      // Herlaad uitsluitend de relevante kalender
      await this.loadNotesForCalendar(note.calendarId);

      // Behoud / selecteer het item
      const targetId = savedNote?.id || note.id;
      this.expandTreeAndSelectNote(savedNote);
    } catch (error) {
      note.dirty = true;
    }
  }

  async selectNoteForEvent(upcoming: EventViewDTO) {
    const newNote = await firstValueFrom(this.noteService.createNoteForUpcomingEvent(upcoming.calendarId, upcoming, this.getTimeZone()));
    this.expandTreeAndSelectNote(newNote);
  }

  async expandTreeAndSelectNote(note: NoteDTO) {
    //expand calendar
    if (!note) return;

    if (note.calendarId !== undefined && note.calendarId !== null) {
      this.expandedCalendarIds.add(note.calendarId);
      if (!this.notesByCalendar.has(note.calendarId)) {
        await this.loadNotesForCalendar(note.calendarId);
      }
    }
    if (note.parentNoteId !== undefined && note.parentNoteId !== null) {
      this.expandedFolderIds.add(note.parentNoteId);
    }
    await this.showNoteDetails(note);

    setTimeout(() => this.scrollIntoView(note), 100);
  }

  /* --- Icons & UI Helpers --- */

  getNodeIcon(node: NoteDTO): string {
    if (node.noteType === 'folder') return 'folder';
    if (node.noteType === 'meeting') return node.recurring ? 'date_range' : 'event';
    return 'subject';
  }

  getEventCardStyle(calendarColor?: string) {

    if (!calendarColor) return {};

    return {
      'background-color': `${calendarColor}0d`, // 26 is hex voor ~15% transparantie
    };

  }

  getResponseIcon(response: string): string {
    const map: any = {
      accepted: 'check_circle', rejected: 'highlight_off', organizer: 'account_circle', confirmed: 'check_circle', tentative: 'help_outline',
      tentativelyAccepted: 'help_outline', notResponded: 'adjust'
    };
    return map[response] || 'circle';
  }

  startSearching() {
    this.isSearching = true;
    this.isAddingRoot = false; // Zorg dat ze niet tegelijk open staan

    // Focus forceren zoals we bij de folder deden
    setTimeout(() => { /* focus logica */ }, 0);
  }

  cancelSearch() {
    this.isSearching = false;
    //TODO this.dataSource.data = this.originalData; // Herstel de volledige boom
  }

  onSearch(query: string) {
    this.searchSubject.next(query);
  }

  // Uitvoering van het zoeken na 300ms rust
  async executeSearch(query: string) {
    this.searchQuery = query.trim().toLowerCase();

    if (this.searchQuery) {
      const loadPromises = this.calendars
        .filter(cal => !this.notesByCalendar.has(cal.id))
        .map(cal => this.loadNotesForCalendar(cal.id));

      await Promise.all(loadPromises);
      this.expandNodesWithMatches();
    }
  }

  private expandNodesWithMatches() {
    for (const cal of this.calendars) {
      const notes = this.notesByCalendar.get(cal.id) || [];
      if (this.hasMatchingNote(notes, this.searchQuery)) {
        this.expandedCalendarIds.add(cal.id);
      }
    }
  }

private hasMatchingNote(nodes: NoteDTO[], query: string): boolean {
    let hasMatchInThisLevel = false;

    for (const node of nodes) {
      const titleMatch = node.title?.toLowerCase().includes(query);
      const textMatch = node.stringText?.toLowerCase().includes(query);

      if (node.noteType === 'folder') {
        const childMatches = node.notes ? this.hasMatchingNote(node.notes, query) : false;
        if (childMatches || titleMatch) {
          this.expandedFolderIds.add(node.id); // Klap de map open
          hasMatchInThisLevel = true;
        }
      } else if (titleMatch || textMatch) {
        hasMatchInThisLevel = true;
      }
    }

    return hasMatchInThisLevel;
  }

  getFilteredNotes(calendarId: number): NoteDTO[] {
    const notes = this.notesByCalendar.get(calendarId) || [];
    if (!this.searchQuery) {
      return notes;
    }
    return this.filterNotesRecursively(notes, this.searchQuery);
  }

  private filterNotesRecursively(nodes: NoteDTO[], query: string): NoteDTO[] {
    return nodes.filter(node => this.isNodeMatching(node, query));
  }

  public isNodeMatching(node: NoteDTO, query: string): boolean {
    if (!query) return true;

    const titleMatch = node.title?.toLowerCase().includes(query) ?? false;
    const textMatch = node.stringText?.toLowerCase().includes(query) ?? false;

    if (node.noteType === 'folder') {
      const childMatch = node.notes?.some(child => this.isNodeMatching(child, query)) ?? false;
      return titleMatch || childMatch;
    }

    return titleMatch || textMatch;
  }

  /*getFilteredNotes(calendarId: number): NoteDTO[] {
    const notes = this.notesByCalendar.get(calendarId) || [];
    if (!this.searchQuery) {
      return notes;
    }
    return this.filterNotesRecursively(notes, this.searchQuery);
  }

private filterNotesRecursively(nodes: NoteDTO[], query: string): NoteDTO[] {
    const filtered: NoteDTO[] = [];

    for (const node of nodes) {
      const titleMatch = node.title?.toLowerCase().includes(query);
      const textMatch = node.stringText?.toLowerCase().includes(query);

      if (node.noteType === 'folder') {
        // Filter recursief de kinderen van de map
        const matchingChildren = node.notes ? this.filterNotesRecursively(node.notes, query) : [];
        
        // Als de map zelf matcht óf matchende kinderen heeft
        if (titleMatch || matchingChildren.length > 0) {
          filtered.push({
            ...node,
            // Toon alleen de kinderen die daadwerkelijk matchen
            notes: matchingChildren
          });
        }
      } else if (titleMatch || textMatch) {
        filtered.push(node);
      }
    }

    return filtered;
  }*/

  clearSearch(input: HTMLInputElement) {
    input.value = '';
    this.searchQuery = '';
  }

  async start_recording(): Promise<void> {
    try {
      // Vraag direct om microfoontoegang (geen schermdeelscherm!)
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Init de MediaRecorder direct op de microfoonstream
      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 64000
      });

      this.audioChunks = [];

      // Vang audio-chunks op per seconde
      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      // Zodra we stoppen: maak er een Blob van en stuur naar afhandeling
      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        this.handleRecordingComplete(audioBlob);
        this.cleanupMediaStream();
      };

      // Start het daadwerkelijk opnemen
      this.mediaRecorder.start(1000);

      // Update UI & Timer
      this.recordingStatus = 'recording';
      this.startTimer();

    } catch (err) {
      console.error('Microfoontoegang geweigerd of niet beschikbaar:', err);
      // Toon eventueel een melding in je UI dat de mic geblokkeerd is
    }
  }

  pause_recording(): void {
    if (!this.mediaRecorder) {
      this.recordingStatus = "paused";
      this.stopTimer();
      return;
    }

    if (this.recordingStatus === 'recording') {
      this.mediaRecorder.pause();
      this.recordingStatus = 'paused';
      this.stopTimer();
    } else if (this.recordingStatus === 'paused') {
      this.mediaRecorder.resume();
      this.recordingStatus = 'recording';
      this.startTimer();
    }
  }

  async stop_recording() {
    const confirmed = await this.functions.confirmDialog("Stop recording", "Are you sure you want to stop the recording and start processing it?");
    if (confirmed) {
      this.mediaRecorder.stop();
      this.recordingStatus = "stopped";
      this.resetTimer();
    }
  }

  async handleRecordingComplete(audioBlob: Blob) {
    // Maak het bestand aan met de .uploading extensie voor de backend atomic staging
    const file = new File([audioBlob], `recording_${Date.now()}.uploading`, {
      type: 'audio/webm'
    });
    const recording = await firstValueFrom(this.recordingService.saveRecording(-1, createRecording(this.user.id, this.currentNote.id, new Date(), "NEW", null)));
    if (recording) {
      const data = await firstValueFrom(this.recordingService.uploadRecording(recording.id, file));
      const updatedRecording = await firstValueFrom(this.recordingService.saveRecording(recording.id, createRecording(this.user.id, this.currentNote.id, recording.recordingDate, "AUDIO UPLOADED", null)));

    }
  }

  private cleanupMediaStream(): void {
    if (this.mediaStream) {
      // Zet de hardware-microfoon écht uit (rode browser-dot verdwijnt)
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }

  formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  private startTimer(): void {
    // Voorkom dubbele intervals als er al een draait
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.timerInterval = setInterval(() => {
      this.recordingSeconds++;
      this.recordingDuration = this.formatTime(this.recordingSeconds);
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private resetTimer(): void {
    this.stopTimer();
    this.recordingSeconds = 0;
    this.recordingDuration = '00:00';
  }

  // Hulpfunctie om seconden netjes als MM:SS te formatteren
  private formatTime(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const pad = (num: number) => num.toString().padStart(2, '0');

    return `${pad(minutes)}:${pad(seconds)}`;
  }

  onHeadingChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    if (value === 'p') {
      this.editor.chain().focus().setParagraph().run();
    } else {
      const level = parseInt(value, 10) as 1 | 2 | 3;
      this.editor.chain().focus().toggleHeading({ level }).run();
    }
  }

  addImage() {
    const url = window.prompt('Afbeelding URL invoeren:');
    if (url) {
      this.editor.chain().focus().setImage({ src: url }).run();
    }
  }

  /* new section section */
  isCalendarExpanded(calendarId: number): boolean {
    return this.expandedCalendarIds.has(calendarId);
  }

  async toggleCalendarSection(cal: CalendarDTO) {

    if (this.expandedCalendarIds.has(cal.id)) {
      // Dichtklappen
      this.expandedCalendarIds.delete(cal.id);
    } else {
      // Openklappen
      this.expandedCalendarIds.add(cal.id);

      // INTELLIGENT LADEN: Pas ophalen uit backend als we deze nog niet eerder geladen hebben!
      if (!this.notesByCalendar.has(cal.id)) {
        await this.loadNotesForCalendar(cal.id);
      }
      this.getUpcomingEvents();
    }
  }

  async loadNotesForCalendar(calendarId: number) {
    this.loadingCalendars.add(calendarId);
    try {
      const notes = await firstValueFrom(this.noteService.getNotes(calendarId));
      const sortedNotes = this.sortNotesWithArchiveAtBottom(notes);
      this.notesByCalendar.set(calendarId, sortedNotes);
    } catch (error) {
      console.error(`Error loading notes for calendarId:  ${calendarId}`, error);
    } finally {
      this.loadingCalendars.delete(calendarId);
    }
  }

  private sortNotesWithArchiveAtBottom(notes: NoteDTO[]): NoteDTO[] {
    if (!notes) return [];

    return notes.sort((a, b) => {
      const isAArchive = a.title?.toLowerCase() === 'archive';
      const isBArchive = b.title?.toLowerCase() === 'archive';

      if (isAArchive && !isBArchive) return 1;  // A (Archive) moet na B komen
      if (!isAArchive && isBArchive) return -1; // A moet voor B (Archive) komen

      return 0; // Behoud de originele volgorde voor overige items
    });
  }

  /* --- Drag & Drop tussen kalenders & mappen --- */
  /** Max 2 lagen diep: alleen toevoegen aan root mappen */
  canAdd(node: NoteDTO): boolean {
    return node.noteType === 'folder' && !node.parentNoteId && !this.archived && !node.systemFolder;
  }

  /* Drag & drop afhandeling tussen kalendersecties en mappen */
  handleDragStart(event: DragEvent, node: NoteDTO, sourceCalendarId: number) {
    event.dataTransfer?.setData('text/plain', JSON.stringify({ noteId: node.id, sourceCalendarId }));
    this.dragNode = node;
  }

  async handleDropOnCalendarOrFolder(event: DragEvent, targetCalendarId: number, targetFolderNode?: NoteDTO) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.dragNode) return;

    // Voorkom dat een folder binnen een andere folder wordt gedropt (Max 2 lagen)
    if (this.dragNode.noteType === 'folder' && targetFolderNode) return;

    const droppedNote = this.dragNode;
    const oldCalendarId = droppedNote.calendarId;

    droppedNote.calendarId = targetCalendarId;
    droppedNote.parentNoteId = targetFolderNode ? targetFolderNode.id : null;
    droppedNote.dirty = true;

    // Verwijder uit oude kalender-lijst
    const oldList = this.notesByCalendar.get(oldCalendarId) || [];
    this.removeFromList(droppedNote, oldList);

    // Voeg toe aan nieuwe kalender-lijst
    if (targetFolderNode) {
      targetFolderNode.notes = targetFolderNode.notes || [];
      targetFolderNode.notes.push(droppedNote);
      this.expandedFolderIds.add(targetFolderNode.id);
    } else {
      const newList = this.notesByCalendar.get(targetCalendarId) || [];
      newList.push(droppedNote);
      this.notesByCalendar.set(targetCalendarId, newList);
    }

    await this.saveNote(droppedNote);
    this.dragNode = null;
    this.cdr.detectChanges();
  }

  isFolderExpanded(folderId: number): boolean {
    return this.expandedFolderIds.has(folderId);
  }

  toggleFolder(folderNode: NoteDTO) {
    if (this.expandedFolderIds.has(folderNode.id)) {
      this.expandedFolderIds.delete(folderNode.id);
    } else {
      this.expandedFolderIds.add(folderNode.id);
    }
  }

  onNodeClick(node: NoteDTO) {
    if (node.noteType === 'folder') {
      this.toggleFolder(node);
    }
    this.showNoteDetails(node);
  }

  addFolderToCalendar(cal: CalendarDTO) {
    // Klap de kalender open als deze nog dicht zat
    if (!this.isCalendarExpanded(cal.id)) {
      this.toggleCalendarSection(cal);
    }

    this.startAddingRoot(cal);
  }

  /** Verbergt een kalender uit het overzicht */
  async hideCalendar(cal: CalendarDTO) {
    cal.showInNotes = false;
    const data = await firstValueFrom(this.calendarService.saveCalendar(cal.id, createCalendar(cal.userId, cal.calendarIntegrationId, cal.externalCalendarId, cal.summary, cal.showInDashboard, cal.showInNotes,
      cal.uuid, cal.taskUuid, cal.color, cal.url)));
    this.calendars = this.calendars.filter(c => c.id !== cal.id);
    this.notesByCalendar.delete(cal.id);
    this.expandedCalendarIds.delete(cal.id);
  }

  /** Werk de kleur van een kalender bij */
  async onCalendarColorChange(cal: CalendarDTO, newColor: string) {
    cal.color = newColor;

    const data = await firstValueFrom(this.calendarService.saveCalendar(cal.id, createCalendar(cal.userId, cal.calendarIntegrationId, cal.externalCalendarId, cal.summary, cal.showInDashboard, cal.showInNotes,
      cal.uuid, cal.taskUuid, cal.color, cal.url)));

    this.cdr.detectChanges();
  }

  async refreshFolderNode(calendarId: number, folderId: number) {
    // 1. Haal de nieuwste gegevens van de map/kinderen op uit de backend
    const updatedFolder = await firstValueFrom(this.noteService.getNote(folderId));

    // 2. Zoek en vervang de map in de Map van de kalender
    const list = this.notesByCalendar.get(calendarId) || [];
    this.replaceNodeInTree(list, updatedFolder);

    // 3. Trigger Change Detection
    this.notesByCalendar.set(calendarId, [...list]);
    this.cdr.detectChanges();
  }

  private replaceNodeInTree(nodes: NoteDTO[], updatedNode: NoteDTO): boolean {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === updatedNode.id) {
        nodes[i] = updatedNode;
        return true;
      }
      if (nodes[i].notes && nodes[i].notes.length > 0) {
        if (this.replaceNodeInTree(nodes[i].notes, updatedNode)) {
          return true;
        }
      }
    }
    return false;
  }

  async refreshCalendarNode(calendarId: number) {
    this.loadingCalendars.add(calendarId);

    try {
      // Haal de verse notities op
      const freshNotes = await firstValueFrom(this.noteService.getNotes(calendarId));

      // Omdat expandedFolderIds (Set<number>) bewaard blijft, blijven geopende mappen open!
      this.notesByCalendar.set(calendarId, freshNotes);
    } catch (error) {
      console.error(`Fout bij refreshen van kalender ${calendarId}:`, error);
    } finally {
      this.loadingCalendars.delete(calendarId);
      this.cdr.detectChanges();
    }
  }

}